import { useEffect, useRef, useState } from "react"
import { Cell, Column, HeaderCell, Table } from "rsuite-table"
import "rsuite-table/dist/css/rsuite-table.css"

// Components
import { toast } from "react-toastify"
import { Card, CardHeader } from "~components/Card"
import {
  formatDateTimeISO,
  removeNulls,
  sanitizeObjectAttributes,
  trimObject,
} from "~utils/utils"
import CopyCell from "~components/CopyCell"
import CloudDownloadIcon from "@mui/icons-material/CloudDownloadOutlined"
import { CSVLink } from "react-csv"
import { PartialRedemption, Redemption } from "~shapes/redemption"
import {
  PERK_REDEMPTION_STATUS,
  REDEMPTION_STATUS_TO_USER_FACING,
} from "~constants/status"
import { ButtonIcon } from "~styled/button"
import CCLRejectionModal from "~components/CCLRejectionModal"
import CCLApprovalModal from "~components/CCLApprovalModal"
import { useAuthContext } from "~context/authContext"
import "./tableStyles.css"
import usePerkRedemptions from "~hooks/usePerkRedemptions"
import { csvHeaders } from "~components/dashboard/csvHeaders"
import FiltersIcon from "@mui/icons-material/Tune"
import moment from "moment"
import ModalFilter from "~components/dashboard/ModalFilter"
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import SortIcon from "@mui/icons-material/SwapVert"
import InfiniteLoader from "~components/dashboard/InfiniteLoader"
import { useFilters } from "~hooks/useFilters"
import isCompanyRole from "~rbac/utils/isCompanyRole"

// Minimum number of visible rows to keep loaded so the table stays scrollable
// and scroll-triggered pagination keeps firing after pre-redeemed rows are
// filtered out client-side for non-admin / CCL users.
const MIN_VISIBLE_ROWS = 15

export interface filtersProps {
  bookingId?: string
  bookingLastName?: string
  bookingFirstName?: string
  status: PERK_REDEMPTION_STATUS | ""
  userEmail?: string
  userId?: string
  proofOfOwnershipName?: string
  updatedByEmail?: string
  createdAtFrom?: string
  createdAtTo?: string
  bookingEventDateFrom?: string
  bookingEventDateTo?: string
  sort?: string
}

enum SORT {
  ASC = "asc",
  DESC = "desc",
}

const RedemptionWidget = () => {
  const { loggedUser, isLoading: authLoading } = useAuthContext()

  const { filters, setFilters, appliedQty } = useFilters(
    loggedUser?.authorities?.roles ?? [],
  )

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [redemptions, setRedemptions] = useState<PartialRedemption[]>([])
  const [isApprovalModalShown, setIsApprovalModalShown] =
    useState<boolean>(false)
  const [isRejectionModalShow, setIsRejectionModalShown] =
    useState<boolean>(false)
  const [selectedRedemptionId, setSelectedRedemptionId] = useState<string>("")
  const [selectedRow, setSelectedRow] = useState<any>(null)
  const [selectedBookingId, setSelectedBookingId] = useState<string>("")
  const loadingTable = useRef(false)
  const [isOpenFiltersModal, setIsOpenFiltersModal] = useState<boolean>(false)
  const [sortType, setSortType] = useState<SORT>(SORT.DESC)

  const {
    refetch: fetchRedemptions,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePerkRedemptions(removeNulls(sanitizeObjectAttributes(filters)))

  const parseRedemptions = (data: Redemption[]) => {
    if (!Array.isArray(data)) return []
    // Non-admin / CCL users should never see pre-redeemed redemptions.
    const visible = isCompanyRole()
      ? data.filter(el => el.status !== PERK_REDEMPTION_STATUS.PRE_REDEEMED)
      : data
    return visible.map((el: Redemption) => {
      const redemptionDate = formatDateTimeISO(el.createdAt)
      const processedDate = formatDateTimeISO(el.updatedAt)

      const createdMoment = moment(el.createdAt)
      const updatedMoment = moment(el.updatedAt)
      const minutesDiff = updatedMoment.diff(createdMoment, "minutes")

      const shouldShowProcessedDate =
        el.status === PERK_REDEMPTION_STATUS.REDEEMED ||
        el.status === PERK_REDEMPTION_STATUS.INVALIDATED

      const action = [
        PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_MANUAL_VALIDATION,
        PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_VALIDATION,
      ].includes(el.status) ? (
        <div className="action-buttons">
          <button
            className="action-btn action-btn-approve"
            onClick={() => {
              setSelectedRedemptionId(el.id)
              setSelectedBookingId(el.bookingId)
              setIsApprovalModalShown(true)
            }}
          >
            Approve
          </button>
          <button
            className="action-btn action-btn-reject"
            onClick={() => {
              setSelectedRedemptionId(el.id)
              setSelectedBookingId(el.bookingId)
              setIsRejectionModalShown(true)
            }}
          >
            Reject
          </button>
        </div>
      ) : null

      const actionState =
        el.status === PERK_REDEMPTION_STATUS.REDEEMED ? (
          <span className="action-state is-success">Completed</span>
        ) : el.status === PERK_REDEMPTION_STATUS.INVALIDATED ? (
          <span className="action-state is-danger">Invalidated</span>
        ) : (
          <span className="action-state is-neutral">No action</span>
        )

      return {
        perkName: el.perkName,
        id: el.id,
        redemptionDate,
        processedDate:
          shouldShowProcessedDate && minutesDiff >= 2 ? processedDate : "",
        createdById: el.createdById,
        userEmail: el.userEmail,
        bookingId: el.bookingId,
        proofOfOwnershipName: el.proofOfOwnershipName,
        bookingEventDate: el.bookingEventDate
          ? formatDateTimeISO(el.bookingEventDate)
          : undefined,
        bookingFirstName: el.bookingFirstName,
        bookingLastName: el.bookingLastName,
        cclShipName: el.cclShipName,
        status: REDEMPTION_STATUS_TO_USER_FACING[el.status] || "",
        cclOnboardCreditCurrency: el.cclOnboardCreditCurrency,
        invalidationDetails: el.invalidationDetails,
        invalidationReason: el.invalidationReason,
        cclOnboardCreditValue: el.cclOnboardCreditValue,
        onboardCredit: el.cclOnboardCreditValue
          ? `${el.cclOnboardCreditCurrency} ${el.cclOnboardCreditValue}`
          : "",
        updatedByEmail: el.updatedByEmail || "",
        rawStatus: el.status,
        action,
        actionState,
      }
    })
  }

  // After filtering out pre-redeemed rows client-side a fetched page may yield
  // very few visible rows. Keep pulling further pages until we have enough
  // visible rows or there are no more pages to fetch.
  const fillRows = async (current: any[], data: any): Promise<any[]> => {
    let acc = current
    let pages = data
    while (
      acc.length < MIN_VISIBLE_ROWS &&
      pages?.pages?.[pages.pages.length - 1]?.hasNextPage
    ) {
      const res = await fetchNextPage()
      pages = res.data
      const lastPage = (res.data?.pages.length ?? 1) - 1
      const next = res.data?.pages?.[lastPage]?.result ?? []
      acc = [...acc, ...parseRedemptions(next)]
    }
    return acc
  }

  const getRedemptions = () => {
    setIsLoading(true)
    fetchRedemptions()
      .then(async res => {
        const first = res.data?.pages?.[0]?.result ?? []
        const filled = await fillRows(parseRedemptions(first), res.data)
        setRedemptions(filled)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    getRedemptions()
  }, [filters])

  useEffect(() => {
    if (isError) {
      toast.error("There was an error getting redemptions data.")
    }
  }, [isError])

  if (authLoading || !loggedUser) {
    return null
  }

  const handleRowClick = (rowData: any) => setSelectedRow(rowData)

  const getRowClassName = (rowData: any) =>
    selectedRow && rowData && rowData.id === selectedRow.id
      ? "selected-row"
      : ""

  const handleScroll = (x: number, y: number) => {
    const top = Math.abs(y)
    const isVerticalScroll = top > Math.abs(x)
    if (!!hasNextPage && isVerticalScroll) {
      const contextHeight = redemptions.length * 46
      if (!loadingTable.current && contextHeight - top - 600 < 50) {
        loadingTable.current = true
        fetchNextPage()
          .then(async res => {
            const lastPage = res.data?.pages.length - 1
            const next = res.data?.pages?.[lastPage]?.result ?? []
            // Top up if this page added very few visible rows, otherwise the
            // table won't grow enough to trigger the next scroll-load.
            const appended = await fillRows(parseRedemptions(next), res.data)
            setRedemptions(prev => [...prev, ...appended])
          })
          .finally(() => {
            loadingTable.current = false
          })
      }
    }
  }

  const search = (form: any) => {
    const next: filtersProps = {
      bookingId: form.bookingId || "",
      bookingLastName: form.bookingLastName || "",
      bookingFirstName: form.bookingFirstName || "",
      status: form.status?.value ?? "",
      userEmail: form.userEmail || "",
      userId: form.userId || "",
      proofOfOwnershipName: form.proofOfOwnershipName || "",
      updatedByEmail: form.updatedByEmail || "",
      createdAtFrom: form.redemptionDate?.[0]
        ? moment(form.redemptionDate[0]).format("YYYY-MM-DD")
        : "",
      createdAtTo: form.redemptionDate?.[1]
        ? moment(form.redemptionDate[1]).format("YYYY-MM-DD")
        : "",
      bookingEventDateFrom: form.sailDate?.[0]
        ? moment(form.sailDate[0]).format("YYYY-MM-DD")
        : "",
      bookingEventDateTo: form.sailDate?.[1]
        ? moment(form.sailDate[1]).format("YYYY-MM-DD")
        : "",
      sort: filters.sort,
    }

    setFilters(trimObject(next) as filtersProps)
  }

  const onSortColumn = (sortColumn: string, currentSortType: SORT) => {
    const nextSort = currentSortType === SORT.DESC ? SORT.ASC : SORT.DESC
    setSortType(nextSort)
    setFilters(prev => ({ ...prev, sort: `${sortColumn},${nextSort}` }))
  }

  const renderSortIcon = () => {
    const iconStyle = { fontSize: 18 }
    if (sortType === SORT.ASC) return <ArrowDropUpIcon style={iconStyle} />
    if (sortType === SORT.DESC) return <ArrowDropDownIcon style={iconStyle} />
    return <SortIcon style={iconStyle} />
  }

  const getStatusClassName = (status: string) => {
    const normalizedStatus = (status || "").toLowerCase()
    if (normalizedStatus.includes("redeemed")) return "is-redeemed"
    if (normalizedStatus.includes("invalidated")) return "is-invalidated"
    if (normalizedStatus.includes("pending")) return "is-pending"
    return "is-default"
  }

  const numberOfRedemptionsShown = redemptions.length

  return (
    <Card padding="20">
      <CardHeader className="redemptions-card-header">
        <div className="redemptions-title">Redemptions 🎉</div>
        {!!redemptions && redemptions.length > 0 && (
          <div className="redemptions-counter">
            Showing: {numberOfRedemptionsShown}
          </div>
        )}
      </CardHeader>

      <div className="redemptions-toolbar">
        <div className="redemptions-toolbar-left">
          <ButtonIcon
            width={"36"}
            height={"36"}
            className="filter-trigger"
            onClick={() => setIsOpenFiltersModal(true)}
            title="Open filters"
          >
            <FiltersIcon />
          </ButtonIcon>
          <div className="applied-filters-pill">
            <span>{appliedQty} filters applied</span>
          </div>
        </div>

        <div className="redemptions-toolbar-right">
          {redemptions.length > 0 && (
            <CSVLink
              filename={"sp-redemptions.csv"}
              className="csv-download-link"
              headers={csvHeaders}
              data={redemptions}
            >
              Export CSV
              <CloudDownloadIcon />
            </CSVLink>
          )}
        </div>
      </div>

      <ModalFilter
        data={filters}
        isOpen={isOpenFiltersModal}
        onSearch={search}
        onClose={setIsOpenFiltersModal}
      />

      {isApprovalModalShown && (
        <CCLApprovalModal
          isOpen={true}
          refreshList={() => getRedemptions()}
          setIsOpen={setIsApprovalModalShown}
          selectedRedemptionId={selectedRedemptionId}
          bookingNumber={selectedBookingId}
        />
      )}
      {isRejectionModalShow && (
        <CCLRejectionModal
          isOpen={true}
          refreshList={() => getRedemptions()}
          setIsOpen={setIsRejectionModalShown}
          selectedRedemptionId={selectedRedemptionId}
          bookingNumber={selectedBookingId}
        />
      )}

      <div className="redemptions-table-wrapper">
        <Table
          className="redemptions-table"
          virtualized={true}
          shouldUpdateScroll={false}
          height={600}
          data={redemptions}
          loading={isLoading}
          onRowClick={rowData => handleRowClick(rowData)}
          rowClassName={getRowClassName}
          onScroll={handleScroll}
          onSortColumn={onSortColumn}
          sortType={sortType}
        >
          <Column key="redemption-id" width={120} resizable>
            <HeaderCell>Redemption ID</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.id} />}</Cell>
          </Column>

          <Column key="user-id" width={100} resizable>
            <HeaderCell>User ID</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.createdById} />}</Cell>
          </Column>

          <Column key="user-email" width={200} resizable>
            <HeaderCell>User Email</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.userEmail} />}</Cell>
          </Column>

          <Column key="broker-statement-name" width={170} resizable>
            <HeaderCell>Broker Statement Name</HeaderCell>
            <Cell>
              {rowData => <CopyCell rowData={rowData.proofOfOwnershipName} />}
            </Cell>
          </Column>

          <Column
            key="redemption-date"
            width={130}
            resizable
            sortable={!!redemptions && redemptions.length > 0}
          >
            <HeaderCell renderSortIcon={renderSortIcon}>
              Redemption Date
            </HeaderCell>
            <Cell dataKey="createdAt">
              {rowData => <CopyCell rowData={rowData.redemptionDate} />}
            </Cell>
          </Column>

          <Column
            key="processed-date"
            width={130}
            resizable
            sortable={!!redemptions && redemptions.length > 0}
          >
            <HeaderCell renderSortIcon={renderSortIcon}>
              Processed Date
            </HeaderCell>
            <Cell dataKey="updatedAt">
              {rowData => <CopyCell rowData={rowData.processedDate} />}
            </Cell>
          </Column>

          <Column key="booking-first-name" width={140} resizable>
            <HeaderCell>Booking First Name</HeaderCell>
            <Cell>
              {rowData => <CopyCell rowData={rowData.bookingFirstName} />}
            </Cell>
          </Column>

          <Column key="booking-last-name" width={140} resizable>
            <HeaderCell>Booking Last Name</HeaderCell>
            <Cell>
              {rowData => <CopyCell rowData={rowData.bookingLastName} />}
            </Cell>
          </Column>

          <Column key="booking-number" width={130} resizable>
            <HeaderCell>Booking Number</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.bookingId} />}</Cell>
          </Column>

          <Column
            key="sail-date"
            width={130}
            resizable
            sortable={!!redemptions && redemptions.length > 0}
          >
            <HeaderCell renderSortIcon={renderSortIcon}>Sail Date</HeaderCell>
            <Cell dataKey="bookingEventDate">
              {rowData => <CopyCell rowData={rowData.bookingEventDate} />}
            </Cell>
          </Column>

          <Column key="ship-name" width={140} resizable>
            <HeaderCell>Ship Name</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.cclShipName} />}</Cell>
          </Column>

          <Column key="brand" width={145} resizable>
            <HeaderCell>Brand</HeaderCell>
            <Cell>{rowData => <CopyCell rowData={rowData.perkName} />}</Cell>
          </Column>

          <Column key="status" width={160} resizable>
            <HeaderCell>Status</HeaderCell>
            <Cell>
              {rowData => (
                <span
                  className={`status-chip ${getStatusClassName(rowData.status)}`}
                >
                  {rowData.status || "-"}
                </span>
              )}
            </Cell>
          </Column>

          {filters.status === PERK_REDEMPTION_STATUS.REDEEMED && (
            <Column key="onboard-credit" width={140} resizable>
              <HeaderCell>Onboard Credit</HeaderCell>
              <Cell>
                {rowData =>
                  rowData.cclOnboardCreditCurrency &&
                  rowData.cclOnboardCreditValue ? (
                    <CopyCell
                      rowData={`${rowData.cclOnboardCreditCurrency} ${rowData.cclOnboardCreditValue}`}
                    />
                  ) : null
                }
              </Cell>
            </Column>
          )}

          {filters.status === PERK_REDEMPTION_STATUS.INVALIDATED && (
            <>
              <Column key="invalidation-reason" width={160} resizable>
                <HeaderCell>Invalidation Reason</HeaderCell>
                <Cell>
                  {rowData => <CopyCell rowData={rowData.invalidationReason} />}
                </Cell>
              </Column>

              <Column key="invalidation-details" width={160} resizable>
                <HeaderCell>Invalidation Details</HeaderCell>
                <Cell>
                  {rowData => (
                    <CopyCell rowData={rowData.invalidationDetails} />
                  )}
                </Cell>
              </Column>
            </>
          )}

          <Column key="updated-by" width={160} resizable>
            <HeaderCell>Updated by</HeaderCell>
            <Cell>
              {rowData => <CopyCell rowData={rowData.updatedByEmail} />}
            </Cell>
          </Column>

          <Column key="action" width={210} fixed={"right"}>
            <HeaderCell>Action</HeaderCell>
            <Cell>
              {rowData => (
                <div className="action-cell">
                  {rowData.action || rowData.actionState}
                </div>
              )}
            </Cell>
          </Column>
        </Table>

        {isFetchingNextPage && <InfiniteLoader />}
      </div>
    </Card>
  )
}

export default RedemptionWidget
